import { useState, useEffect } from 'react';
import api from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import Card, { CardHeader, CardTitle } from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Tabs from '../components/ui/Tabs';
import { ClipboardDocumentIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'snapshot', label: 'Create Snapshot' },
  { id: 'history', label: 'History' },
];

export default function AdminKillSwitch() {
  const [activeTab, setActiveTab] = useState('overview');
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Snapshot form state
  const [globalEnabled, setGlobalEnabled] = useState(false);
  const [farmToggles, setFarmToggles] = useState({});
  const [snapshotNotes, setSnapshotNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Debug state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isDebug, setIsDebug] = useState(false);
  const [showAdminSecret, setShowAdminSecret] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [lastCreatedSnapshot, setLastCreatedSnapshot] = useState(null);

  // History state
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);

  useEffect(() => {
    fetchState();
  }, []);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab, historyPage]);

  const fetchState = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/auth/admin/kill-switch');
      const data = res.data.data;
      setState(data);

      // Initialize farm toggles from latest snapshot
      const toggles = {};
      if (data.latestSnapshot?.farmSettings) {
        for (const fs of data.latestSnapshot.farmSettings) {
          const farmId = fs.farm?._id || fs.farm;
          toggles[farmId] = fs.enabled;
        }
      }
      // Add new farms as inactive
      if (data.newFarms) {
        for (const farm of data.newFarms) {
          toggles[farm._id] = false;
        }
      }
      setFarmToggles(toggles);
      setGlobalEnabled(data.latestSnapshot?.globalEnabled || false);
    } catch (err) {
      setError('Failed to load kill switch state');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get(`/auth/admin/kill-switch/history?page=${historyPage}&limit=10`);
      setHistory(res.data.data?.docs || []);
      setHistoryTotal(res.data.data?.total || 0);
    } catch (err) {
      console.error('Failed to load history', err);
    }
  };

  const [activatingCookie, setActivatingCookie] = useState(false);
  const [cookieResult, setCookieResult] = useState(null);
  const [debugCookieFarmId, setDebugCookieFarmId] = useState('');

  const handleActivateDebugCookie = async () => {
    if (!debugCookieFarmId) return;
    try {
      setActivatingCookie(true);
      setCookieResult(null);
      const res = await api.get(`/auth/admin/kill-switch/debug-cookie?farmId=${debugCookieFarmId}`);
      const cookies = res.data.data?.cookiesSet || [];
      const layers = cookies.map((c) => c.layer).join(', ');
      setCookieResult({ success: true, message: `${cookies.length} cookie(s) set: ${layers} (1 hour)` });
    } catch (err) {
      setCookieResult({
        success: false,
        message: err.response?.data?.message || 'Failed to activate debug cookie',
      });
    } finally {
      setActivatingCookie(false);
      setTimeout(() => setCookieResult(null), 5000);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const openConfirmModal = () => {
    setIsDebug(false);
    setShowConfirmModal(true);
  };

  const handleCreateSnapshot = async () => {
    const farmSettings = Object.entries(farmToggles).map(([farmId, enabled]) => ({
      farmId,
      enabled,
    }));

    setShowConfirmModal(false);

    try {
      setSubmitting(true);
      const res = await api.post('/auth/admin/kill-switch', {
        globalEnabled,
        farmSettings,
        notes: snapshotNotes || null,
        ...(isDebug ? { isDebug: true } : {}),
      });
      // Store the full response to capture debugKey/debugSecret
      if (res.data.data) {
        setLastCreatedSnapshot(res.data.data);
      }
      setSnapshotNotes('');
      setIsDebug(false);
      setShowAdminSecret(false);
      await fetchState();
      setActiveTab('overview');
    } catch (err) {
      alert('Failed to create snapshot: ' + (err.response?.data?.message || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;
  if (error) return <div className="text-center py-8 text-red-600">{error}</div>;

  // Build a map of all farms (from snapshot + new farms)
  const allFarms = [];
  if (state?.latestSnapshot?.farmSettings) {
    for (const fs of state.latestSnapshot.farmSettings) {
      const farm = fs.farm || {};
      allFarms.push({
        _id: farm._id || fs.farm,
        name: farm.name || 'Unknown Farm',
        domains: farm.domains || [],
      });
    }
  }
  if (state?.newFarms) {
    for (const farm of state.newFarms) {
      allFarms.push(farm);
    }
  }

  const totalPages = Math.ceil(historyTotal / 10);

  return (
    <div>
      <PageHeader
        title="Kill Switch (Admin)"
        description="Emergency mechanism to block customer access across all farms. Manages Layer 2 global snapshots."
      />

      <Tabs tabs={TABS} activeTab={activeTab} onChange={setActiveTab} className="mb-6" />

      {/* ─── Overview Tab ────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Layer 1: ENV */}
          <Card>
            <CardHeader>
              <CardTitle>Layer 1: Environment Variable</CardTitle>
              <Badge variant={state?.envKillSwitch ? 'danger' : 'success'} size="lg" dot>
                {state?.envKillSwitch ? 'ACTIVE' : 'Inactive'}
              </Badge>
            </CardHeader>
            <p className="text-sm text-slate-500">
              {state?.envKillSwitch
                ? 'ENV_KILL_SWITCH is set to true. All customer traffic is blocked at the infrastructure level. This requires a deployment change to modify.'
                : 'ENV_KILL_SWITCH is not active. Normal operations.'}
            </p>
          </Card>

          {/* Layer 2: Global Snapshot */}
          <Card>
            <CardHeader>
              <CardTitle>Layer 2: Global Snapshot</CardTitle>
              <Badge variant={state?.latestSnapshot?.globalEnabled ? 'danger' : 'success'} size="lg" dot>
                {state?.latestSnapshot?.globalEnabled ? 'GLOBAL BLOCK' : 'Per-Farm'}
              </Badge>
            </CardHeader>

            {state?.latestSnapshot ? (
              <div className="text-sm text-slate-500 mb-4">
                Last snapshot by{' '}
                <span className="font-medium text-slate-700">
                  {state.latestSnapshot.triggeredBy?.name || state.latestSnapshot.triggeredBy?.email || 'Unknown'}
                </span>
                {' '}on {new Date(state.latestSnapshot.createdAt).toLocaleString()}
                {state.latestSnapshot.notes && (
                  <span className="block mt-1 text-slate-600 italic">"{state.latestSnapshot.notes}"</span>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-500 mb-4">No snapshot created yet.</p>
            )}

            {/* Debug Credentials */}
            {(() => {
              const snapshot = lastCreatedSnapshot?.isDebug ? lastCreatedSnapshot : state?.latestSnapshot;
              const hasCredentials = snapshot?.isDebug && (snapshot?.debugKey || snapshot?.debugSecret);
              if (!hasCredentials) return null;
              return (
                <div className="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50">
                  <h4 className="text-sm font-semibold text-slate-900 mb-2">Debug Credentials</h4>
                  <p className="text-xs text-slate-500 mb-3">
                    Set a cookie with <code className="bg-slate-100 px-1 rounded">name=debugKey</code> and <code className="bg-slate-100 px-1 rounded">value=debugSecret</code> to bypass the kill switch.
                  </p>
                  <div className="space-y-2">
                    {snapshot.debugKey && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-slate-500 w-16 shrink-0">Key:</span>
                        <code className="text-xs bg-white px-2 py-1 rounded font-mono flex-1 truncate border border-slate-200">
                          {snapshot.debugKey}
                        </code>
                        <button
                          onClick={() => copyToClipboard(snapshot.debugKey, 'adminKey')}
                          className="p-1 text-slate-400 hover:text-slate-600 shrink-0"
                          title="Copy key"
                        >
                          <ClipboardDocumentIcon className="h-4 w-4" />
                        </button>
                        {copiedField === 'adminKey' && <span className="text-xs text-green-600">Copied!</span>}
                      </div>
                    )}
                    {snapshot.debugSecret && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-slate-500 w-16 shrink-0">Secret:</span>
                        <code className="text-xs bg-white px-2 py-1 rounded font-mono flex-1 truncate border border-slate-200">
                          {showAdminSecret ? snapshot.debugSecret : '••••••••••••••••'}
                        </code>
                        <button
                          onClick={() => setShowAdminSecret(!showAdminSecret)}
                          className="p-1 text-slate-400 hover:text-slate-600 shrink-0"
                          title={showAdminSecret ? 'Hide secret' : 'Show secret'}
                        >
                          {showAdminSecret ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => copyToClipboard(snapshot.debugSecret, 'adminSecret')}
                          className="p-1 text-slate-400 hover:text-slate-600 shrink-0"
                          title="Copy secret"
                        >
                          <ClipboardDocumentIcon className="h-4 w-4" />
                        </button>
                        {copiedField === 'adminSecret' && <span className="text-xs text-green-600">Copied!</span>}
                      </div>
                    )}
                  </div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <select
                      value={debugCookieFarmId}
                      onChange={(e) => setDebugCookieFarmId(e.target.value)}
                      className="px-2 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="">Select farm...</option>
                      {allFarms.map((farm) => (
                        <option key={farm._id} value={farm._id}>{farm.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={handleActivateDebugCookie}
                      disabled={activatingCookie || !debugCookieFarmId}
                      className="px-3 py-1.5 text-xs font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                    >
                      {activatingCookie ? 'Setting cookies...' : 'Activate Debug Cookie'}
                    </button>
                    {cookieResult && (
                      <span className={`text-xs ${cookieResult.success ? 'text-green-600' : 'text-red-600'}`}>
                        {cookieResult.message}
                      </span>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Per-farm resolved states */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-slate-700 mb-3">Farm Resolved States</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-slate-500">
                      <th className="pb-2 pr-4 font-medium">Farm</th>
                      <th className="pb-2 pr-4 font-medium">Layer 2</th>
                      <th className="pb-2 pr-4 font-medium">Layer 3 Override</th>
                      <th className="pb-2 font-medium">Resolved</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {allFarms.map((farm) => {
                      const farmId = farm._id?.toString();
                      const resolved = state?.resolvedStates?.[farmId];
                      const l2Setting = state?.latestSnapshot?.farmSettings?.find(
                        (fs) => (fs.farm?._id || fs.farm)?.toString() === farmId
                      );
                      const l3Override = state?.farmOverrides?.find(
                        (fo) => fo.farm?.toString() === farmId
                      );
                      const isNew = state?.newFarms?.some((f) => f._id?.toString() === farmId);

                      return (
                        <tr key={farmId}>
                          <td className="py-2.5 pr-4">
                            <span className="font-medium text-slate-700">{farm.name}</span>
                            {isNew && (
                              <Badge variant="info" size="sm" className="ml-2">New</Badge>
                            )}
                          </td>
                          <td className="py-2.5 pr-4">
                            {l2Setting ? (
                              <Badge variant={l2Setting.enabled ? 'danger' : 'success'} size="sm">
                                {l2Setting.enabled ? 'Blocked' : 'Active'}
                              </Badge>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="py-2.5 pr-4">
                            {l3Override ? (
                              <Badge variant={l3Override.enabled ? 'danger' : 'success'} size="sm">
                                {l3Override.enabled ? 'Blocked' : 'Active'}
                              </Badge>
                            ) : (
                              <span className="text-slate-400">None</span>
                            )}
                          </td>
                          <td className="py-2.5">
                            <Badge
                              variant={resolved?.blocked ? 'danger' : 'success'}
                              size="sm"
                              dot
                            >
                              {resolved?.blocked
                                ? `Blocked (${resolved.reason})`
                                : 'Active'}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ─── Create Snapshot Tab ─────────────────────────────── */}
      {activeTab === 'snapshot' && (
        <Card>
          <CardHeader>
            <CardTitle>Create New Snapshot (Layer 2)</CardTitle>
          </CardHeader>
          <p className="text-sm text-slate-500 mb-6">
            Snapshots are immutable. Creating a new snapshot replaces the current state.
            All active farms must be included.
          </p>

          {/* Global toggle */}
          <div className="mb-6 p-4 rounded-lg border border-slate-200 bg-slate-50">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-semibold text-slate-900">Global Kill Switch</h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  When enabled, ALL customer traffic is blocked regardless of per-farm settings.
                </p>
              </div>
              <button
                onClick={() => setGlobalEnabled(!globalEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  globalEnabled
                    ? 'bg-red-600 focus:ring-red-500'
                    : 'bg-slate-300 focus:ring-slate-500'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    globalEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Per-farm toggles */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-slate-900 mb-3">Per-Farm Settings</h4>
            <div className="space-y-2">
              {allFarms.map((farm) => {
                const farmId = farm._id?.toString();
                const isBlocked = farmToggles[farmId] || false;

                return (
                  <div
                    key={farmId}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      isBlocked ? 'border-red-200 bg-red-50' : 'border-slate-200'
                    }`}
                  >
                    <div>
                      <span className="text-sm font-medium text-slate-700">{farm.name}</span>
                      {farm.domains?.length > 0 && (
                        <span className="text-xs text-slate-400 ml-2">
                          ({farm.domains.join(', ')})
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() =>
                        setFarmToggles((prev) => ({ ...prev, [farmId]: !prev[farmId] }))
                      }
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                        isBlocked
                          ? 'bg-red-600 focus:ring-red-500'
                          : 'bg-slate-300 focus:ring-slate-500'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          isBlocked ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Notes (optional)
            </label>
            <textarea
              value={snapshotNotes}
              onChange={(e) => setSnapshotNotes(e.target.value)}
              placeholder="Reason for this snapshot..."
              maxLength={1000}
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Submit */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <button
              onClick={() => {
                // Reset to current state
                const toggles = {};
                if (state?.latestSnapshot?.farmSettings) {
                  for (const fs of state.latestSnapshot.farmSettings) {
                    toggles[fs.farm?._id || fs.farm] = fs.enabled;
                  }
                }
                if (state?.newFarms) {
                  for (const farm of state.newFarms) {
                    toggles[farm._id] = false;
                  }
                }
                setFarmToggles(toggles);
                setGlobalEnabled(state?.latestSnapshot?.globalEnabled || false);
                setSnapshotNotes('');
              }}
              className="px-4 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50"
            >
              Reset
            </button>
            <button
              onClick={openConfirmModal}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Creating...' : 'Create Snapshot'}
            </button>
          </div>
        </Card>
      )}

      {/* ─── History Tab ─────────────────────────────────────── */}
      {activeTab === 'history' && (
        <Card>
          <CardHeader>
            <CardTitle>Snapshot History</CardTitle>
            <span className="text-sm text-slate-500">{historyTotal} total</span>
          </CardHeader>

          {history.length === 0 ? (
            <p className="text-sm text-slate-500">No snapshots yet.</p>
          ) : (
            <>
              <div className="space-y-4">
                {history.map((snapshot) => (
                  <div
                    key={snapshot._id}
                    className="p-4 rounded-lg border border-slate-200"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={snapshot.globalEnabled ? 'danger' : 'default'}
                          size="sm"
                        >
                          {snapshot.globalEnabled ? 'Global Block' : 'Per-Farm'}
                        </Badge>
                        {snapshot.isDebug && (
                          <Badge variant="warning" size="sm">Debug</Badge>
                        )}
                        {snapshot.farmSettings?.some((fs) => fs.enabled) && !snapshot.globalEnabled && (
                          <Badge variant="warning" size="sm">
                            {snapshot.farmSettings.filter((fs) => fs.enabled).length} farm(s) blocked
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-slate-500">
                        {new Date(snapshot.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <div className="text-sm text-slate-600">
                      By{' '}
                      <span className="font-medium text-slate-700">
                        {snapshot.triggeredBy?.name || snapshot.triggeredBy?.email || 'Unknown'}
                      </span>
                    </div>
                    {snapshot.notes && (
                      <p className="text-sm text-slate-500 italic mt-1">"{snapshot.notes}"</p>
                    )}
                    {/* Farm breakdown */}
                    {snapshot.farmSettings?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {snapshot.farmSettings.map((fs, i) => (
                          <Badge
                            key={i}
                            variant={fs.enabled ? 'danger' : 'success'}
                            size="sm"
                          >
                            {fs.farm?.name || 'Unknown'}: {fs.enabled ? 'Blocked' : 'Active'}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <span className="text-sm text-slate-500">
                    Page {historyPage} of {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                      disabled={historyPage === 1}
                      className="px-3 py-1 text-sm border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setHistoryPage((p) => Math.min(totalPages, p + 1))}
                      disabled={historyPage === totalPages}
                      className="px-3 py-1 text-sm border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (() => {
        const farmSettings = Object.entries(farmToggles).map(([farmId, enabled]) => ({
          farmId,
          enabled,
        }));
        const blockedCount = farmSettings.filter((fs) => fs.enabled).length;
        const action = globalEnabled
          ? 'GLOBALLY BLOCK all customer traffic'
          : blockedCount > 0
            ? `block ${blockedCount} farm(s)`
            : 'set all farms as active';

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Create Kill Switch Snapshot
              </h3>
              <p className="text-sm text-slate-600 mb-2">
                This will <span className="font-semibold">{action}</span>.
              </p>
              <p className="text-sm text-slate-500 mb-4">
                This action cannot be undone (snapshots are immutable).
              </p>

              <label className="flex items-center gap-2 mb-4 p-3 rounded-lg border border-slate-200 bg-slate-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isDebug}
                  onChange={(e) => setIsDebug(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <span className="text-sm font-medium text-slate-700">Enable debug mode</span>
                  <p className="text-xs text-slate-500">Generates cookie-based bypass credentials</p>
                </div>
              </label>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateSnapshot}
                  className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Snapshot
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
